<template>
  <article>
    <MastheadAlt
      subtitle="Link in bio for Organizations"
      title="Sophisticated member management tools"
      text="Add your team to your organization and control their access to your link in bio profiles."
      ctaText="(10-day trial included with every paid plan)"
    >
      <Button href="https://app.campsite.bio/create-account" size="large"
        >Get started</Button
      >
      <Button variant="outline" to="/demo" size="large">Request a Demo</Button>
    </MastheadAlt>

    <SeenOn />

    <TestimonialSection
      title="We are loving campsite"
      img-src="/campsites/small-business/campsite-smallstudio.png"
      name="Ashton Henry, CEO, Small Studio"
    >
      <p>
        We are loving campsite and have started integrating it for all of our
        clients as well. We love the analytics we get on our pro accounts that
        help us to determine what is truly working on social media. We are able
        to take that information from previous months and use it to create
        successful data driven strategies! If you're a social media manager or
        just someone who wants to better understand how to make social media
        work harder for your business, you need campsite.
      </p>
    </TestimonialSection>

    <LargeTextSection>
      As an organization, you need the tools to control your team's access to
      your profiles. Campsite.bio makes it easy with our sophisticated member
      management tools. Manage all of your accounts in a single place with a
      single invoice.
    </LargeTextSection>

    <FeatureSection title="Control who has access to your profiles">
      <VText slot="text"
        >Our organization settings allow you to fine tune your access control.
        You can restrict access to all profiles, give direct access, restrict
        member profile creation, and more.</VText
      >

      <FeatureCard
        span="2"
        title="Member management tools"
        bg-color="secondary"
        color="secondary"
      >
        <PersonAddIcon slot="icon" />
        <VText
          >Invite, remove, and manage your team members from a centralized
          place. Then control their access to your profiles with our
          organization settings.
        </VText>
        <img
          slot="img"
          src="~/assets/images/use-cases/organizations/org-members.png"
          loading="lazy"
          alt="Organization members"
        />
      </FeatureCard>
    </FeatureSection>

    <FeatureSection title="Supercharge your link in bio" bg-color="gray100">
      <VText slot="text">
        Get a link in bio tool with the power of an organization.
      </VText>
      <Button slot="cta" to="/pricing?plans=organizations"
        >Compare features</Button
      >

      <FeatureCard
        span="2"
        title="Universal links"
        bg-color="secondary"
        color="secondary"
      >
        <WebsiteIcon slot="icon" />
        <VText
          >Create sets of links that are shared across profiles from a single
          source. Save time and resources by managing shared links in one place.
        </VText>
        <img
          slot="img"
          src="~/assets/images/use-cases/organizations/universal-links.png"
          loading="lazy"
          alt="Universal link settings"
        />
      </FeatureCard>

      <FeatureCard title="Multiple owners">
        <AccountMultipleIcon slot="icon" />
        <VText
          >Add multiple owners to your organization to make sure you never lose
          access.
        </VText>
      </FeatureCard>
      <FeatureCard title="Profile management">
        <LinkIcon slot="icon" />
        <VText
          >Manage all of your accounts in one place with a single invoice. Add,
          remove, and switch between profiles with ease; without having to log
          into separate accounts.
        </VText>
      </FeatureCard>
      <FeatureCard title="Default member roles">
        <SvgIcon slot="icon" viewBox="0 0 24 24">
          <path
            d="M12,12H19C18.47,16.11 15.72,19.78 12,20.92V12H5V6.3L12,3.19M12,1L3,5V11C3,16.55 6.84,21.73 12,23C17.16,21.73 21,16.55 21,11V5L12,1Z"
          />
        </SvgIcon>
        <VText
          >Set the default access role your members have on your profiles. We
          make it easy to control member access to your profiles.</VText
        >
      </FeatureCard>
      <FeatureCard title="Outside collaborators">
        <LoginIcon slot="icon" />
        <VText
          >Give outside collaborators access to your profiles without having to
          add them to your organization. This is great for agencies and
          contractors.
        </VText>
      </FeatureCard>
      <FeatureCard title="Automation">
        <SettingsIcon slot="icon" />
        <VText
          >Keep your profile fresh with our automated link types, like the feed
          and image grid. They'll pull in your newest content, automatically.
        </VText>
      </FeatureCard>
      <FeatureCard title="Custom domain">
        <WebsiteIcon slot="icon" />
        <VText>
          You already have a domain, why not use it for you bio link? Integrate
          your domain right into your profile (ex. links.mydomain.com).
        </VText>
      </FeatureCard>
    </FeatureSection>

    <RaisedCtaSection
      title="Simplify your link in bio management today"
      text="All of your accounts in one easy to manage place."
    >
      <Button href="https://app.campsite.bio/create-account" size="large"
        >Get started</Button
      >
    </RaisedCtaSection>
  </article>
</template>

<script>
import {
  VText,
  Button,
  LineChartIcon,
  AccountMultipleIcon,
  PersonAddIcon,
  SettingsIcon,
  WebsiteIcon,
  LinkIcon,
  SvgIcon,
  LoginIcon,
} from '@campsite-bio/component-lib';

import SeenOn from '@/components/seen-on-analytics';
import TestimonialSection from '@/components/testimonials/testimonial-section';
import FeatureCard from '@/components/features/feature-card';
import FeatureSection from '@/components/features/feature-section';
import RaisedCtaSection from '@/components/sections/raised-cta-section';
import LargeTextSection from '@/components/sections/large-text-section';
import MastheadAlt from '@/components/sections/masthead-alt';
import { generateMeta } from '~/utils/generate-meta';

export default {
  components: {
    VText,
    Button,
    SeenOn,
    TestimonialSection,
    FeatureCard,
    FeatureSection,
    LineChartIcon,
    AccountMultipleIcon,
    SettingsIcon,
    RaisedCtaSection,
    LargeTextSection,
    MastheadAlt,
    WebsiteIcon,
    LinkIcon,
    SvgIcon,
    LoginIcon,
    PersonAddIcon,
  },

  head() {
    return generateMeta({
      title: 'Link in bio for Organizations - Campsite.bio',
      description:
        'Prove your ROI on your link in bio. Learn more about the features Campsite.bio offers to organizations.',
    });
  },
};
</script>

<style lang="scss" scoped></style>
